import { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  Edit2,
  XCircle,
  Clock,
  Plus,
  CheckCircle2,
  Trash2,
  RotateCcw,
  Lock
} from 'lucide-react';
import toast from 'react-hot-toast';
import certificateTemplateService from '@/services/certificate-template.service';
import {
  CertificateTemplateType,
  CertificateTemplateFilterDto,
  CERTIFICATE_TEMPLATE_TYPE_LABELS,
  CertificateTemplateDto,
  CertificateTemplateUsage,
  isSystemTemplate
} from '@/types/certificate-template.types';
import { CardSkeleton } from '@/components/ui/SkeletonLoader';
import TooltipArrow from '@/components/ui/TooltipArrow';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import SearchInputDebounced from '@/components/ui/SearchInputDebounced';
import Pagination from '@/components/ui/Pagination';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { CERTIFICATE_TEMPLATE_PERMISSION } from '@/constants/constant-permissions';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import CreateTemplateModal from '@/components/certificate-builder/CreateTemplateModal';
import UseTemplateConfirmModal from '@/components/certificate-builder/UseTemplateConfirmModal';
import RestoreTemplateModal from '@/components/certificate-builder/RestoreTemplateModal';
import { invalidateTemplateQueries } from '@/components/certificate-builder/templateQueries';

/** Strip Handlebars block helpers for clean preview display */
function cleanHtmlForPreview(html: string): string {
  return html.replace(
    /\{\{#if\s+\w+\}\}([\s\S]*?)(?:\{\{else\}\}[\s\S]*?)?\{\{\/if\}\}/g,
    '$1'
  );
}

const ALL_TEMPLATE_TYPES = Object.values(CertificateTemplateType);

/** Template id in use per type; a type still loading or failed is left out */
function combineInUse(results: UseQueryResult<CertificateTemplateDto | null>[]) {
  const map: Partial<Record<CertificateTemplateType, string | null>> = {};
  results.forEach((r, i) => { if (r.isSuccess) map[ALL_TEMPLATE_TYPES[i]] = r.data?._id ?? null; });
  return map;
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** "1 certification" / "2 courses" that picked this template in Edit Settings; '' when none (or the backend sent no usage) */
function assignedTo(usage: CertificateTemplateUsage | undefined): string {
  if (!usage) return '';
  return [
    usage.courses > 0 && plural(usage.courses, 'course'),
    usage.learning_paths > 0 && plural(usage.learning_paths, 'learning path'),
    usage.certifications > 0 && plural(usage.certifications, 'certification')
  ].filter(Boolean).join(', ');
}

export default function CertificateTemplateList() {
  usePermissionRouteAccess([
    CERTIFICATE_TEMPLATE_PERMISSION.ADMIN_CERTIFICATE_TEMPLATE_READ,
    CERTIFICATE_TEMPLATE_PERMISSION.ADMIN_CERTIFICATE_TEMPLATE_WRITE,
  ]);
  const canWrite = useHasPermission([CERTIFICATE_TEMPLATE_PERMISSION.ADMIN_CERTIFICATE_TEMPLATE_WRITE]);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [templateToUse, setTemplateToUse] = useState<CertificateTemplateDto | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<CertificateTemplateDto | null>(null);

  // Filter state
  const [filters, setFilters] = useState<CertificateTemplateFilterDto>({
    is_active: undefined,
    template_type: undefined,
    search: '',
    include_deleted: false,
    page: 1,
    limit: 10
  });


  // Select options for template type filter
  const templateTypeOptions: SelectOption[] = useMemo(() => [
    { label: 'All Types', value: 'all' },
    ...Object.entries(CERTIFICATE_TEMPLATE_TYPE_LABELS).map(([value, label]) => ({
      label,
      value
    }))
  ], []);

  // Fetch templates (paginated)
  const { data: paginatedData, isLoading, error } = useQuery({
    queryKey: ['certificateTemplates', filters],
    // Usage counts lock Delete on assigned templates; not needed in the Trash
    queryFn: () => certificateTemplateService.getAllTemplates({ ...filters, include_usage: !filters.only_deleted }),
  });

  const templates = Array.isArray(paginatedData?.templates)
    ? paginatedData.templates
    : Array.isArray(paginatedData) ? paginatedData : [];
  const totalItems = paginatedData?.total ?? 0;
  const totalPages = paginatedData?.total_pages ?? 1;

  // Template each type issues with today (same lookup as the backend), keyed by type
  // One query per type (same key as the "Use this template" popup) so a switch refreshes only that type
  const inUseByType = useQueries({
    queries: ALL_TEMPLATE_TYPES.map(type => ({
      queryKey: ['certificateTemplates', 'in-use', type],
      queryFn: () => certificateTemplateService.getTemplateByType(type)
    })),
    combine: combineInUse
  });

  const isInUse = useCallback((template: CertificateTemplateDto) => {
    const inUseId = inUseByType?.[template.template_type];
    // Lookup not loaded (or failed) for this type → fall back to the default flag
    return inUseId !== undefined ? inUseId === template._id : template.is_default && template.is_active;
  }, [inUseByType]);

  // Trash: deleted templates stay in the database and can be restored
  const isDeletedView = !!filters.only_deleted;
  const [templateToRestore, setTemplateToRestore] = useState<CertificateTemplateDto | null>(null);
  const { data: deletedCount } = useQuery({
    queryKey: ['certificateTemplates', 'deleted-count'],
    queryFn: async () => (await certificateTemplateService.getAllTemplates({ only_deleted: true, page: 1, limit: 1 }))?.total ?? 0
  });
  const switchView = useCallback((deleted: boolean) => {
    setFilters(prev => ({ ...prev, only_deleted: deleted || undefined, page: 1 }));
  }, []);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => certificateTemplateService.deleteTemplate(id),
    onSuccess: () => {
      toast.success(`Template "${templateToDelete?.template_name}" moved to Deleted. You can restore it from there.`);
      // Last card on a later page → go back one page
      if (templates.length === 1 && (filters.page || 1) > 1) {
        setFilters(prev => ({ ...prev, page: (prev.page || 1) - 1 }));
      }
      // In-use lookup only matters if the deleted one was in use (backend then falls back)
      if (templateToDelete) {
        invalidateTemplateQueries(queryClient, templateToDelete.template_type, { inUse: isInUse(templateToDelete), deletedCount: true });
      }
      setTemplateToDelete(null);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to delete template');
    }
  });

  // Handlers
  const handleEdit = useCallback((id: string) => {
    navigate(`/admin/enterprise/certificate-templates/${id}/edit`);
  }, [navigate]);

  const handleSearchChange = useCallback((value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  }, []);

  const handleTypeFilterChange = useCallback((value: string | number | null) => {
    setFilters(prev => ({
      ...prev,
      template_type: value === 'all' || !value ? undefined : (value as CertificateTemplateType),
      page: 1
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const handleItemsPerPageChange = useCallback((limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 1 }));
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  const getTypeBadge = (type: CertificateTemplateType) => {
    const colors: Record<CertificateTemplateType, string> = {
      [CertificateTemplateType.COURSE_COMPLETION]: 'bg-blue-100 text-blue-700',
      [CertificateTemplateType.LEARNING_PATH_COMPLETION]: 'bg-purple-100 text-purple-700',
      [CertificateTemplateType.CERTIFICATION_GOLD]: 'bg-amber-100 text-amber-700',
      [CertificateTemplateType.CERTIFICATION_SILVER]: 'bg-gray-200 text-gray-700',
      [CertificateTemplateType.CERTIFICATION_BRONZE]: 'bg-orange-100 text-orange-700'
    };

    const colorClass = colors[type] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${colorClass}`}>
        {CERTIFICATE_TEMPLATE_TYPE_LABELS[type]}
      </span>
    );
  };

  const getBadgePreview = (template: CertificateTemplateDto) => {
    const tierColors: Record<string, { bg: string; text: string; label: string }> = {
      [CertificateTemplateType.CERTIFICATION_GOLD]: { bg: '#FFD700', text: '#000000', label: 'GOLD' },
      [CertificateTemplateType.CERTIFICATION_SILVER]: { bg: '#C0C0C0', text: '#000000', label: 'SILVER' },
      [CertificateTemplateType.CERTIFICATION_BRONZE]: { bg: '#CD7F32', text: '#FFFFFF', label: 'BRONZE' }
    };

    const tier = tierColors[template.template_type];
    if (!tier) return null;

    return (
      <span
        className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded"
        style={{ backgroundColor: tier.bg, color: tier.text }}
      >
        {tier.label}
      </span>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-xl p-4 md:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <Award className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Certificate Templates</h1>
              <p className="text-sm sm:text-base text-brand-amber-100 hidden sm:block">
                Manage certificate templates for courses, certifications, and achievements
              </p>
            </div>
          </div>
          {canWrite && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-brand-orange-600 bg-white rounded-lg hover:bg-brand-orange-50 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Create Template
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 md:p-6">
        {/* Templates / Deleted (Trash) */}
        <div className="inline-flex p-1 mb-4 bg-gray-100 rounded-lg" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={!isDeletedView}
            onClick={() => switchView(false)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${!isDeletedView ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Templates
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isDeletedView}
            onClick={() => switchView(true)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${isDeletedView ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Deleted{deletedCount ? ` (${deletedCount})` : ''}
          </button>
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1">
            <SearchInputDebounced
              value={filters.search}
              onChange={handleSearchChange}
              placeholder="Search by name or key..."
              debounceDelay={500}
              loading={isLoading}
            />
          </div>

          {/* Template Type Filter */}
          <div className="min-w-[200px]">
            <SimpleSelect
              value={filters.template_type || 'all'}
              onChange={handleTypeFilterChange}
              options={templateTypeOptions}
              placeholder="Select Type"
              isSearchable={false}
            />
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-medium">Failed to load certificate templates</p>
          <p className="text-red-600 text-sm mt-1">{(error as Error).message}</p>
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white border rounded-lg p-12 text-center">
          <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{isDeletedView ? 'No deleted templates' : 'No templates found'}</h3>
          <p className="text-gray-600">
            {filters.search || filters.template_type
              ? 'Try adjusting your filters'
              : isDeletedView
                ? 'Templates you delete appear here, so you can restore them.'
                : 'No certificate templates available'}
          </p>
          {canWrite && !isDeletedView && !filters.search && !filters.template_type && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Template
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => {
            const assigned = assignedTo(template.used_by);
            // Built-ins never get a Delete icon (permanent rule, shown by the Built-in chip).
            // Assigned ones keep a disabled icon: the admin can fix that, so say how.
            const isBuiltIn = isSystemTemplate(template);
            const deleteLockReason = assigned ? `Can't delete: this template is assigned to ${assigned}. Change it in their settings first.` : '';
            return (
            <div
              key={template._id}
              className="bg-white rounded-lg border hover:shadow-lg transition-all duration-200 overflow-hidden group"
            >
              {/* Card Header - Preview Area */}
              <div
                className={`h-52 bg-gradient-to-br from-gray-800 to-gray-900 border-b flex items-center justify-center relative overflow-hidden ${isDeletedView ? 'opacity-70' : canWrite ? 'cursor-pointer' : 'cursor-not-allowed opacity-80'}`}
                onClick={() => !isDeletedView && canWrite && handleEdit(template._id)}
                title={isDeletedView ? 'Restore this template to edit it' : !canWrite ? 'You do not have permission to edit' : undefined}
              >
                {/* Certificate Preview - Render actual html_template */}
                {template.html_template ? (
                  <div className="w-full h-full flex items-center justify-center p-3">
                    <div
                      className="rounded shadow-lg overflow-hidden transform group-hover:scale-105 transition-transform"
                      style={{
                        width: 842 * 0.28,
                        height: 595 * 0.28,
                      }}
                    >
                      <iframe
                        srcDoc={cleanHtmlForPreview(template.html_template)}
                        title={`Preview: ${template.template_name}`}
                        className="border-0 pointer-events-none bg-white"
                        style={{
                          width: 842,
                          height: 595,
                          transform: 'scale(0.28)',
                          transformOrigin: 'top left',
                        }}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="w-48 h-28 bg-white rounded shadow-sm border flex flex-col items-center justify-center p-2 transform group-hover:scale-105 transition-transform">
                    {getBadgePreview(template)}
                    <p className="text-[8px] font-bold text-gray-800 mt-1 text-center leading-tight">
                      Certificate Template
                    </p>
                    <p className="text-[6px] text-gray-500 mt-0.5">{template.template_name}</p>
                  </div>
                )}

                {/* Only one status: the Default of its type (what new certificates use); Deleted in the Trash */}
                {(isDeletedView || isInUse(template)) && (
                  <div className="absolute top-2 right-2 z-10">
                    {isDeletedView ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full shadow">
                        <Trash2 className="w-3 h-3" />
                        Deleted
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-600 text-white rounded-full shadow">
                        <CheckCircle2 className="w-3 h-3" />
                        Default
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Card Body */}
              <div className="p-4">
                <div className="mb-3">
                  <h3 className="font-semibold text-gray-900 truncate">{template.template_name}</h3>
                  <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 inline-block mt-1">
                    {template.template_key}
                  </code>
                </div>

                {/* Type, Status and Date */}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {getTypeBadge(template.template_type)}
                    {/* What the template is (fixed), separate from Default (which one is used now) */}
                    {isBuiltIn && (
                      <TooltipArrow
                        content={isDeletedView
                          // Only built-ins deleted before they were protected can be here
                          ? 'System built-in template. Restore it to use or edit it again.'
                          : "System built-in template. It can't be deleted, but you can edit it or choose another default."}
                        position="top"
                        delay={150}
                        className="w-64"
                      >
                        <span
                          data-built-in-badge
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-full cursor-default"
                        >
                          <Lock className="w-3 h-3" />
                          Built-in
                        </span>
                      </TooltipArrow>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{formatDate(template.updated_at)}</span>
                  </div>
                </div>
              </div>

              {/* Card Footer - Quick Actions */}
              {isDeletedView ? (
              <div className="px-4 pb-4">
                <button
                  onClick={() => setTemplateToRestore(template)}
                  disabled={!canWrite}
                  title={!canWrite ? 'You do not have permission to restore templates' : undefined}
                  className="w-full py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCcw className="w-4 h-4" />
                  Restore
                </button>
              </div>
              ) : (
              <div className="px-4 pb-4 flex items-center gap-2">
                <button
                  onClick={() => handleEdit(template._id)}
                  disabled={!canWrite}
                  title={!canWrite ? 'You do not have permission to edit' : undefined}
                  className="flex-1 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Template
                </button>
                {/* Switch / delete only for templates not in use (the in-use one can't be deleted) */}
                {!isInUse(template) && (
                  <>
                    <button
                      onClick={() => setTemplateToUse(template)}
                      disabled={!canWrite}
                      title={!canWrite ? 'You do not have permission to change templates' : `Use this template for ${CERTIFICATE_TEMPLATE_TYPE_LABELS[template.template_type as CertificateTemplateType]} certificates`}
                      className="flex-1 py-2 text-sm font-medium text-brand-green-700 bg-brand-green-50 rounded-lg hover:bg-brand-green-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Use this
                    </button>
                    {/* Backend refuses to delete an assigned template; say why on hover */}
                    {!isBuiltIn && (
                    <TooltipArrow
                      content={deleteLockReason}
                      position="left"
                      delay={150}
                      className="w-64"
                    >
                      <button
                        onClick={() => setTemplateToDelete(template)}
                        disabled={!canWrite || !!deleteLockReason}
                        title={!canWrite ? 'You do not have permission to delete' : deleteLockReason ? undefined : 'Delete template'}
                        aria-label="Delete template"
                        // A disabled button gets no hover events; let the tooltip wrapper receive them
                        className={`p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${deleteLockReason ? 'pointer-events-none' : ''}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TooltipArrow>
                    )}
                  </>
                )}
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !error && templates.length > 0 && (
        <Pagination
          currentPage={filters.page || 1}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={filters.limit || 10}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
          itemsPerPageOptions={[10, 20, 50, 100]}
        />
      )}

      <CreateTemplateModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />

      <UseTemplateConfirmModal
        isOpen={!!templateToUse}
        template={templateToUse}
        onClose={() => setTemplateToUse(null)}
      />

      <DeleteConfirmationModal
        isOpen={!!templateToDelete}
        onClose={() => !deleteMutation.isPending && setTemplateToDelete(null)}
        onConfirm={() => templateToDelete && deleteMutation.mutate(templateToDelete._id)}
        title="Delete Template"
        message={`Delete "${templateToDelete?.template_name}"? It moves to Deleted, where you can restore it later. If a course, learning path or certification has chosen it, you will be asked to change that first.`}
        isLoading={deleteMutation.isPending}
      />

      <RestoreTemplateModal
        template={templateToRestore}
        onClose={() => setTemplateToRestore(null)}
        // Last card on a later Trash page → go back one page (same as delete)
        onRestored={() => { if (templates.length === 1 && (filters.page || 1) > 1) setFilters(prev => ({ ...prev, page: (prev.page || 1) - 1 })); }}
      />
    </div>
  );
}
